import {
    MEDIA_GROUP_MAX_PER_ROW,
    MEDIA_GROUP_MAX_ROWS,
    parseMediaGroupLayout,
    type MediaGroupDocType,
    type MediaGroupLayout
} from '@ast-data/shared';

/** The most pictures one plate can hold: every row full. */
export const MEDIA_GROUP_MAX_PICTURES =
    MEDIA_GROUP_MAX_PER_ROW * MEDIA_GROUP_MAX_ROWS;

/**
 * How `count` pictures are split into rows to start with: as few rows as keep
 * them large, and as even as they go — 5 as 3 + 2, 6 as 3 + 3, 7 as 4 + 3,
 * 9 as three rows of 3. Only a start; any split may be dragged into instead.
 */
export function suggestRowLengths(count: number): number[] {
    const pictures = Math.min(Math.max(0, count), MEDIA_GROUP_MAX_PICTURES);
    if (pictures === 0) {
        return [];
    }
    const rowCount =
        pictures <= 3
            ? 1
            : Math.max(2, Math.ceil(pictures / MEDIA_GROUP_MAX_PER_ROW));
    const base = Math.floor(pictures / rowCount);
    const longer = pictures % rowCount;
    return Array.from({ length: rowCount }, (_row, index) =>
        index < longer ? base + 1 : base
    );
}

/** Pictures in order, poured into rows of the given lengths. */
export function fillRows(
    ids: readonly string[],
    lengths: readonly number[]
): MediaGroupLayout {
    const rows: MediaGroupLayout = [];
    let next = 0;
    for (const length of lengths) {
        const row = ids.slice(next, next + length);
        next += length;
        if (row.length > 0) {
            rows.push(row);
        }
    }
    return rows;
}

/** A plate's place for one picture: which plate, and which cell in it. */
export interface GroupSlot {
    group: MediaGroupDocType;
    /** Zero-based cell, row by row. */
    cell: number;
}

/**
 * Which plate each picture of ONE description sits on.
 *
 * Only pictures the description still shows count (`shownIds`): a plate keeps
 * the id of a picture that was deleted or unlinked since, and that cell simply
 * closes up. Two devices grouping the same picture offline can put it on two
 * plates; the older plate keeps it, so it never prints twice.
 */
export function groupSlots(
    groups: readonly MediaGroupDocType[],
    shownIds: ReadonlySet<string>
): Map<string, GroupSlot> {
    const slots = new Map<string, GroupSlot>();
    for (const group of sortGroups(groups)) {
        let cell = 0;
        for (const mediaId of parseMediaGroupLayout(group.layout).flat()) {
            if (!shownIds.has(mediaId) || slots.has(mediaId)) {
                continue;
            }
            slots.set(mediaId, { group, cell });
            cell += 1;
        }
    }
    return slots;
}

/**
 * Plates in the order they print in: their `sortOrder`, and among equals
 * (every plate from before it could be moved) the order they were made in.
 */
export function sortGroups(
    groups: readonly MediaGroupDocType[]
): MediaGroupDocType[] {
    return [...groups].sort(
        (firstGroup, secondGroup) =>
            (firstGroup.sortOrder ?? 0) - (secondGroup.sortOrder ?? 0) ||
            firstGroup.createdAt.localeCompare(secondGroup.createdAt) ||
            firstGroup.id.localeCompare(secondGroup.id)
    );
}

/** One plate with the pictures it still holds, in cell order. */
export interface ResolvedGroup<Item> {
    group: MediaGroupDocType;
    members: Item[];
}

/**
 * Split one description's pictures into those printed on their own and the
 * plates, each with its members in cell order. Singles keep the order they
 * came in; a plate that holds nothing any more is left out.
 */
export function partitionByGroups<Item>(
    items: readonly Item[],
    idOf: (item: Item) => string,
    slots: ReadonlyMap<string, GroupSlot>
): { singles: Item[]; groups: ResolvedGroup<Item>[] } {
    const singles: Item[] = [];
    const byGroup = new Map<
        string,
        { group: MediaGroupDocType; cells: [number, Item][] }
    >();
    for (const item of items) {
        const slot = slots.get(idOf(item));
        if (!slot) {
            singles.push(item);
            continue;
        }
        const entry = byGroup.get(slot.group.id) ?? {
            group: slot.group,
            cells: []
        };
        entry.cells.push([slot.cell, item]);
        byGroup.set(slot.group.id, entry);
    }
    const groups = sortGroups(
        [...byGroup.values()].map((entry) => entry.group)
    ).map((group) => ({
        group,
        members: byGroup
            .get(group.id)!
            .cells.sort(([firstCell], [secondCell]) => firstCell - secondCell)
            .map(([, item]) => item)
    }));
    return { singles, groups };
}

/**
 * A plate's members laid out in its rows. A picture no longer there leaves its
 * row shorter; a row left with nothing is dropped.
 */
export function plateRows<Item>(
    group: MediaGroupDocType,
    members: readonly Item[],
    idOf: (item: Item) => string
): Item[][] {
    const byId = new Map(members.map((item) => [idOf(item), item]));
    return parseMediaGroupLayout(group.layout)
        .map((row) =>
            row.flatMap((id) => {
                const item = byId.get(id);
                return item ? [item] : [];
            })
        )
        .filter((row) => row.length > 0);
}
